import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

export type AnalyticsExportFormat = "csv" | "xlsx";

export type AnalyticsTabExportRegistration = {
  canExport: boolean;
  formats?: AnalyticsExportFormat[];
  onExport: (format: AnalyticsExportFormat) => void | Promise<void>;
};

type AnalyticsTabExportContextValue = {
  registration: AnalyticsTabExportRegistration | null;
  setRegistration: (
    registration: AnalyticsTabExportRegistration | null,
  ) => void;
};

const AnalyticsTabExportContext = createContext<AnalyticsTabExportContextValue>(
  {
    registration: null,
    setRegistration: () => undefined,
  },
);

export function AnalyticsTabExportProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [registration, setRegistration] =
    useState<AnalyticsTabExportRegistration | null>(null);

  const value = useMemo(
    () => ({
      registration,
      setRegistration,
    }),
    [registration],
  );

  return (
    <AnalyticsTabExportContext.Provider value={value}>
      {children}
    </AnalyticsTabExportContext.Provider>
  );
}

export function useAnalyticsTabExport(): AnalyticsTabExportContextValue {
  return useContext(AnalyticsTabExportContext);
}

export function useRegisterAnalyticsTabExport(
  registration: AnalyticsTabExportRegistration,
): void {
  const { setRegistration } = useAnalyticsTabExport();

  const clearRegistration = useCallback(() => {
    setRegistration(null);
  }, [setRegistration]);

  useLayoutEffect(() => {
    setRegistration(registration);
    return clearRegistration;
  }, [clearRegistration, registration, setRegistration]);
}
